import {
  convertImageToBuffer,
  buildHeader,
  buildParagraph,
  buildNewlines,
  buildPageBreak,
} from "./helpers";
import { Document, Paragraph, ImageRun, TextRun, AlignmentType } from "docx";
import { calculateWeekNumber } from "./utils/weekUtils";

// Curriculum Icons
import { CurriculumIcons } from "../../constants/CurriculumIcons";

// Images
import amiraStandingPose from "../../images/poses/amira_standing.png";



const getCurriculumLogo = (weeklyPlan) => {
  try {
    // Get curriculum name from weekly plan
    const curriculumName = weeklyPlan?.curriculum?.name;
    
    if (!curriculumName) {
      return CurriculumIcons.default;
    }
    
    // Convert curriculum name to the format used in CurriculumIcons
    // Examples: "EL" -> "el", "EPS SPIRE" -> "eps_spire", "Kiddom EL" -> "kiddomel"
    const normalizedName = curriculumName
      .toLowerCase()
      .replace(/\s+/g, "_")  // Replace spaces with underscores
      .replace(/[^a-z0-9_]/g, ""); // Remove special characters
    
    // Look up the icon, fallback to default if not found
    const curriculumIcon = CurriculumIcons[normalizedName] || CurriculumIcons.default;
    return curriculumIcon;
  } catch (error) {
    console.error("Error getting curriculum logo:", error);
    return CurriculumIcons.default;
  }
};

const createTitlePage = async (studentData, weeklyPlan, units, focuses) => {
  // Get dynamic curriculum logo based on weekly plan
  const curriculumLogo = getCurriculumLogo(weeklyPlan);
  
  // Load images first
  const curriculumLogoBuffer = await convertImageToBuffer(curriculumLogo);
  const amiraBuffer = await convertImageToBuffer(amiraStandingPose);
  
  // Calculate week number based on school year start date
  const weekNumber = await calculateWeekNumber();

  return [
    // Dynamic Curriculum Logo with Amira Avatar
    createDocumentLogo(curriculumLogoBuffer, amiraBuffer),
    // Individualized Reading Instruction Plan
    buildHeader("Individualized Reading Instruction Plan", 1),
    // For:   Student First Name
    buildHeader(`For: ${studentData.firstName}`, 2),
    ...buildNewlines(2),
    // Week X
    buildParagraph(`Week ${weekNumber}`, { bold: true }),
    // Newlines
    ...buildNewlines(3),
    // Curriculum Summary - spread the array of paragraphs
    ...createCurriculumSummary(units, focuses),
    // Page Break
    buildPageBreak(),
  ];
};

const createDocumentLogo = (curriculumLogoBuffer, amiraBuffer) => {
  return new Paragraph({
    alignment: AlignmentType.CENTER,
    children: [
      new ImageRun({
        type: "png",
        data: curriculumLogoBuffer,
        transformation: {
          width: 240,  // Reduced from 280 to fit better within margins
          height: 154, // Proportionally reduced from 180
        },
        altText: {
          title: "Curriculum Logo",
          description: "Curriculum Logo",
          name: "Curriculum Logo",
        },
      }),
      new TextRun({
        text: "      with      ",
        size: 24,
        bold: true,
      }),
      new ImageRun({
        type: "png",
        data: amiraBuffer,
        transformation: {
          width: 60,   // Reduced from 73
          height: 220, // Reduced from 266 to prevent cutoff
        },
        altText: {
          title: "Amira Avatar",
          description: "Amira Avatar",
          name: "Amira Avatar",
        },
      }),
    ],
    spacing: {
      after: 2400,
      before: 0,
      line: 600,        // Increased from 480 to accommodate taller images
      lineRule: "atLeast", // Changed from "exact" to allow flexibility
    },
  });
};

const createCurriculumSummary = (units, focuses) => {
  const paragraphs = [];
  
  // Working In section
  paragraphs.push(buildParagraph(`Working In: `, { bold: true }));
  
  // For each unit, show the unit name and its lessons
  units.forEach((unit) => {
    // Unit name
    paragraphs.push(buildParagraph(`-${unit.name}`, { bold: true }));
    
    // Unit lessons as bullet points with descriptions
    if (unit.lessons && unit.lessons.length > 0) {
      unit.lessons.forEach((lesson) => {
        if (lesson.description) {
          paragraphs.push(buildParagraph(`  • ${lesson.description}`));
        }
      });
    }
  });
  
  return paragraphs;
};

export default createTitlePage;
